import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as SelectPrimitive from '@radix-ui/react-select';
import { AlertCircle, Check, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import type React from 'react';
import { createContext, forwardRef, useContext, useId } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

// iOS-style grouped lists: an uppercase header, a rounded cell of rows with
// inset hairline dividers, and a muted footer. The phone menu editor and the
// Settings → Voice page are both built from these, so the page and the sheet
// read as one calm, consistent surface. (phone-menu/ui/Grouped.tsx re-exports
// this file.)

/**
 * The rounded cell surface, for anything that draws a group-like card itself
 * (a route card, a call map menu). Dark mode lifts it off the sheet body.
 * The ring is full strength (voice round 1, F19): at /60 the cells were
 * 1.04:1 against the canvas, and on phones they sit straight on the page.
 */
export const GROUP_CELL_CLASS =
	'rounded-xl bg-background ring-1 ring-border dark:bg-zinc-900 dark:ring-white/10';

/**
 * The tone for 13px captions and group footers and the 12px group titles
 * (voice round 1, F19): zinc-600 keeps them above 4.5:1 on the darker
 * canvas, where muted-foreground drops to about 4.4:1.
 */
export const CAPTION_TONE_CLASS = 'text-zinc-600 dark:text-zinc-400';

/**
 * Row text: 15px on phones, 14px from 640px (voice round 1, F7).
 */
export const ROW_TEXT_CLASS = 'text-[15px] sm:text-sm';

/**
 * A row's hover tint, only where a pointer can hover (voice round 1, F7): on
 * a touch screen a plain `hover:` tint stays on the tapped row and makes an
 * unchecked row look chosen. Pair it with `active:bg-muted/60`.
 */
export const ROW_HOVER_CLASS = '[@media(hover:hover)]:hover:bg-muted/40';

/** The 2px keyboard ring with an offset, for buttons that sit on a surface (voice round 1, F18). */
export const BUTTON_FOCUS_RING_CLASS =
	'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/**
 * One header for every sheet (DetailSheet and the phone menu sheet, voice
 * round 2 F16): Cancel, Back or Finish later on the left, the title in the
 * middle, Done on the right. The title is 17px on phones (15px from 640px)
 * and truncates between the buttons. Header buttons are 15px on phones
 * (size="sm" is 12px), 44px tall on touch screens, dim on hover only where
 * a pointer can hover, and have the dialogs' 2px focus ring. Done adds
 * font-semibold.
 */
export const SHEET_HEADER_CLASS =
	'grid h-14 shrink-0 grid-cols-[1fr_auto_1fr] items-center border-b border-border/60 bg-background/85 px-2 backdrop-blur';
export const SHEET_TITLE_CLASS = 'min-w-0 truncate px-1 text-center text-[17px] font-semibold outline-none sm:text-[15px]';
export const SHEET_HEADER_BUTTON_CLASS = cn(
	'h-11 min-w-11 px-3 text-[15px] font-medium hover:bg-transparent active:text-foreground/60 sm:h-8 sm:text-sm touch:h-11',
	'[@media(hover:hover)]:hover:text-foreground/70',
	BUTTON_FOCUS_RING_CLASS,
);

/**
 * A text box that fills its cell (voice round 2, F18): no box of its own
 * inside the cell's box, the row's 16px insets, 16px text on phones (so iOS
 * doesn't zoom in), and the rows' inset keyboard ring. Put it on a
 * <Textarea> straight inside a cell, or inside a CELL_FIELD_BELOW_ROW_CLASS
 * wrapper when a row sits above it in the same cell.
 */
export const CELL_TEXTAREA_CLASS = cn(
	'block min-h-24 w-full resize-none rounded-none border-0 bg-transparent px-4 py-3 text-base shadow-none sm:text-sm',
	'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
);

/** The hairline between a row and the text box under it in the same cell, inset like the dividers. */
export const CELL_FIELD_BELOW_ROW_CLASS =
	'relative before:pointer-events-none before:absolute before:left-4 before:right-0 before:top-0 before:h-px before:bg-border/70';

/**
 * Dialogs over the sheet (turn on/off, finish changes, add before closing):
 * inset from the screen edges with rounded corners on phones, and buttons
 * with 44px touch targets (round 3, F18). Put on AlertDialogContent and on
 * each footer button.
 */
export const SHEET_DIALOG_CLASS = 'max-sm:w-[calc(100%-32px)] max-sm:rounded-2xl';
export const DIALOG_BUTTON_CLASS = cn('h-11 sm:h-9 touch:h-11', BUTTON_FOCUS_RING_CLASS);

/**
 * A link inside a sentence (round 6, F13): it stays inline with the words
 * around it at their line height, underlined so it doesn't rely on weight
 * alone (WCAG 1.4.1), and its 44px-tall hit area comes from a pseudo-element
 * (as SuggestionChips does), so it never stretches the line. Put the
 * separator and the link in one `whitespace-nowrap` span so they don't break
 * apart. A link that closes the sheet ends with ArrowUpRight, not ›, and says
 * "(closes the phone menu)" in its aria-label. The link itself never wraps
 * ("Edit / hours" split its hit area in two), and 14px above and below a
 * 13px line makes the target 44px (voice round 1, F7).
 */
export const INLINE_LINK_CLASS = cn(
	'relative inline whitespace-nowrap rounded-sm p-0 font-medium text-foreground underline decoration-foreground/40 underline-offset-2',
	"before:absolute before:-inset-x-1 before:-inset-y-[14px] before:content-['']",
	'hover:decoration-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
);

/**
 * The heading level for group titles below this point. Pages sit under the
 * sheet's h2, so groups are h3; setup steps have their own h3 step heading,
 * so they provide 'h4' here.
 */
export const GroupHeadingLevel = createContext<'h3' | 'h4'>('h3');

const GROUP_TITLE_CLASS = cn('text-xs font-medium uppercase tracking-wide', CAPTION_TONE_CLASS);

export interface GroupProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title' | 'children'> {
	id?: string;
	title?: React.ReactNode;
	footer?: React.ReactNode;
	children?: React.ReactNode;
	className?: string;
	cellClassName?: string;
	// No cell background (for content that draws its own surface, or for
	// several <GroupCell>s in one group: `bare cellClassName="space-y-2"`).
	bare?: boolean;
	// Overrides GroupHeadingLevel.
	headingLevel?: 'h3' | 'h4';
	// E.g. 'sr-only' where the page or step title already says it.
	titleClassName?: string;
	// The right side of the header, e.g. the group's <GroupStatus>.
	headerTrailing?: React.ReactNode;
	// Id for the footer, so a switch row can point aria-describedby at it.
	footerId?: string;
}

// Any other prop (data-phone-menu-target, data-settings-row, …) goes on the <section>.
export const Group = ({
	id,
	title,
	footer,
	children,
	className,
	cellClassName,
	bare = false,
	headingLevel,
	titleClassName,
	headerTrailing,
	footerId,
	...sectionProps
}: GroupProps) => {
	const headingId = useId();
	const inherited = useContext(GroupHeadingLevel);
	const Heading = headingLevel ?? inherited;
	return (
		<section
			id={id}
			aria-labelledby={title ? headingId : undefined}
			className={cn('scroll-mt-20', className)}
			{...sectionProps}
		>
			{headerTrailing ? (
				<div className="flex min-h-5 items-center justify-between gap-3 px-4 pb-1.5">
					{title ? (
						<Heading id={headingId} className={cn('min-w-0', GROUP_TITLE_CLASS, titleClassName)}>
							{title}
						</Heading>
					) : (
						<span />
					)}
					<div className="flex shrink-0 items-center">{headerTrailing}</div>
				</div>
			) : (
				title && (
					<Heading id={headingId} className={cn('px-4 pb-1.5', GROUP_TITLE_CLASS, titleClassName)}>
						{title}
					</Heading>
				)
			)}
			{children !== undefined && children !== null && children !== false && (
				<div
					className={cn(
						!bare && cn('overflow-hidden', GROUP_CELL_CLASS),
						cellClassName,
					)}
				>
					{children}
				</div>
			)}
			{footer && (
				<div id={footerId} className={cn('px-4 pt-1.5 text-[13px] leading-snug', CAPTION_TONE_CLASS)}>
					{footer}
				</div>
			)}
		</section>
	);
};

/**
 * One rounded cell, for a group that holds several (the Voice page's Who
 * answers: Right now, the choices and the details, 8px apart). Put them in a
 * `<Group bare cellClassName="space-y-2">`.
 */
export const GroupCell = ({
	children,
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode }) => (
	<div className={cn('overflow-hidden', GROUP_CELL_CLASS, className)} {...props}>
		{children}
	</div>
);

/**
 * A static subheader inside a cell ("ADVANCED"), not a control. Inside a
 * <CheckList> (a <ul>) render it `as="li"`. `inset` starts it where the
 * titles of rows with a leading slot start (56px).
 */
export const GroupSubheader = ({
	children,
	id,
	className,
	inset = false,
	as: Element = 'div',
}: {
	children: React.ReactNode;
	id?: string;
	className?: string;
	inset?: boolean;
	as?: 'div' | 'li';
}) => (
	<Element id={id} className={cn('px-4 pb-1 pt-3', inset && 'pl-14', GROUP_TITLE_CLASS, className)}>
		{children}
	</Element>
);

const dividerClass =
	'after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:h-px after:bg-border/70 last:after:hidden';

const hasContent = (node: React.ReactNode) => node !== undefined && node !== null && node !== false && node !== '';

export const IconTile = ({ icon: Icon, className }: { icon: React.ElementType; className?: string }) => (
	<span
		aria-hidden
		className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-foreground', className)}
	>
		<Icon className="h-4 w-4" />
	</span>
);

/**
 * The 28px leading slot rows share (voice round 2, F18), so every title in
 * a cell starts at the same place: an icon tile (`icon`), something else
 * such as a <DigitChip> (`leading`), or, with `inset` alone, nothing at all.
 * Any of the three also starts the row's divider at 52px, as an icon row's
 * does.
 */
export interface LeadingSlotProps {
	icon?: React.ElementType;
	leading?: React.ReactNode;
	inset?: boolean;
}

const hasLeadingSlot = ({ icon, leading, inset }: LeadingSlotProps) => Boolean(icon || hasContent(leading) || inset);

const leadingDividerClass = (slot: LeadingSlotProps) => (hasLeadingSlot(slot) ? 'after:left-[52px]' : 'after:left-4');

const LeadingSlot = ({ icon, leading, inset }: LeadingSlotProps) => {
	if (icon) return <IconTile icon={icon} />;
	if (hasContent(leading)) return <span className="flex w-7 shrink-0 items-center justify-center">{leading}</span>;
	return inset ? <span aria-hidden className="w-7 shrink-0" /> : null;
};

/**
 * A static row (holds controls). For a leading icon, chip or blank slot use
 * `icon`, `leading` or `inset` (LeadingSlotProps) rather than putting one in
 * the children, so the title lines up and the divider starts under it.
 */
export const Row = ({
	children,
	className,
	icon,
	leading,
	inset = false,
	id,
}: LeadingSlotProps & {
	children: React.ReactNode;
	className?: string;
	id?: string;
}) => (
	<div
		id={id}
		className={cn(
			'relative flex min-h-11 items-center gap-3 px-4 py-2.5',
			ROW_TEXT_CLASS,
			dividerClass,
			leadingDividerClass({ icon, leading, inset }),
			className,
		)}
	>
		<LeadingSlot icon={icon} leading={leading} inset={inset} />
		{children}
	</div>
);

export interface RowButtonProps
	extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title' | 'value'>,
		LeadingSlotProps {
	icon?: React.ElementType;
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	value?: React.ReactNode;
	chevron?: boolean;
	// Amber "needs finishing" treatment for the value.
	attention?: boolean;
	destructive?: boolean;
	// For a row that opens in place (not a page): sets aria-expanded and
	// turns the chevron down while open.
	expanded?: boolean;
	// Below 640px the value always goes on a line under the title, whatever
	// its length (rows in a list that should line up, e.g. the phone menu's
	// options). From 640px it sits on the right as usual.
	stackOnPhone?: boolean;
	// A muted 13px line under the value (e.g. the number behind "Your business
	// phone"): right-aligned while the value is on the right, under it on the
	// left when the value is stacked under the title on a phone.
	valueCaption?: React.ReactNode;
}

/** A tappable row that pushes a page or opens something. */
// Short titles stay whole and the value takes the rest of the line ("After
// hours · Say you're closed, then…"). Below 640px a long text value (or any
// value with `stackOnPhone`) moves to a muted second line instead of being
// cut to a few characters.
const LONG_VALUE = 18;

export const RowButton = forwardRef<HTMLButtonElement, RowButtonProps>(
	(
		{
			icon,
			leading,
			inset = false,
			title,
			subtitle,
			value,
			chevron = true,
			attention = false,
			destructive = false,
			expanded,
			stackOnPhone = false,
			valueCaption,
			className,
			...props
		},
		ref,
	) => {
		const hasValue = value !== undefined && value !== null && value !== '';
		const hasValueCaption = hasValue && hasContent(valueCaption);
		const wrapsOnPhone =
			hasValue && (stackOnPhone || (typeof value === 'string' && value.length > LONG_VALUE));
		// With a subtitle the title column needs to shrink (its subtitle truncates).
		const titleClass = !hasValue
			? !destructive && 'flex-1'
			: subtitle
				? 'flex-1'
				: wrapsOnPhone
					? 'flex-1 sm:max-w-[70%] sm:flex-none sm:shrink-0'
					: 'max-w-[70%] shrink-0';
		const dot = attention && <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />;
		const valueTone = attention ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground';
		const caption = hasValueCaption && (
			<span className={cn('mt-0.5 block max-w-full truncate text-[13px] leading-snug', CAPTION_TONE_CLASS)}>
				{valueCaption}
			</span>
		);
		return (
			<button
				ref={ref}
				type="button"
				aria-expanded={expanded}
				className={cn(
					'relative flex min-h-11 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
					ROW_TEXT_CLASS,
					ROW_HOVER_CLASS,
					'active:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
					dividerClass,
					leadingDividerClass({ icon, leading, inset }),
					destructive &&
						'justify-center text-red-600 dark:text-red-400 [@media(hover:hover)]:hover:text-red-700 dark:[@media(hover:hover)]:hover:text-red-300',
					className,
				)}
				{...props}
			>
				<LeadingSlot icon={icon} leading={leading} inset={inset} />
				<span className={cn('min-w-0', titleClass)}>
					<span className="block truncate">{title}</span>
					{subtitle && (
						<span className={cn('mt-0.5 block text-[13px] leading-snug', CAPTION_TONE_CLASS)}>{subtitle}</span>
					)}
					{wrapsOnPhone && (
						<span className="mt-0.5 block sm:hidden">
							<span className={cn('flex items-center gap-1.5', valueTone)}>
								{dot}
								<span className="truncate">{value}</span>
							</span>
							{caption}
						</span>
					)}
				</span>
				{hasValue && (
					<span
						className={cn(
							'text-right',
							subtitle ? 'ml-auto max-w-[55%]' : 'min-w-0 flex-1',
							wrapsOnPhone ? 'hidden sm:flex' : 'flex',
							hasValueCaption ? 'flex-col items-end' : 'items-center justify-end gap-1.5',
							valueTone,
						)}
					>
						{hasValueCaption ? (
							<>
								<span className="flex max-w-full items-center justify-end gap-1.5">
									{dot}
									<span className="truncate">{value}</span>
								</span>
								{caption}
							</>
						) : (
							<>
								{dot}
								<span className="truncate">{value}</span>
							</>
						)}
					</span>
				)}
				{chevron && !destructive && (
					<ChevronRight
						aria-hidden
						className={cn(
							'h-4 w-4 shrink-0 text-muted-foreground/80 transition-transform motion-reduce:transition-none',
							expanded && 'rotate-90',
						)}
					/>
				)}
			</button>
		);
	},
);
RowButton.displayName = 'RowButton';

/**
 * A single-choice list of rows with a trailing check (iOS style), on Radix
 * RadioGroup so arrow keys work. Put <ChoiceRow>s inside.
 */
export const ChoiceList = ({
	value,
	onValueChange,
	children,
	label,
	disabled,
	className,
}: {
	value: string;
	onValueChange: (value: string) => void;
	children: React.ReactNode;
	label: string;
	disabled?: boolean;
	className?: string;
}) => (
	<RadioGroupPrimitive.Root
		value={value}
		onValueChange={onValueChange}
		aria-label={label}
		disabled={disabled}
		className={className}
	>
		{children}
	</RadioGroupPrimitive.Root>
);

export const ChoiceRow = ({
	value,
	icon,
	title,
	caption,
	selected,
	disabled,
	disabledReason,
	children,
	detailClassName,
	below,
}: {
	value: string;
	icon?: React.ElementType;
	title: React.ReactNode;
	caption?: React.ReactNode;
	selected: boolean;
	disabled?: boolean;
	disabledReason?: React.ReactNode;
	// Detail controls shown under the row while it's selected.
	children?: React.ReactNode;
	// Replaces the details' padding, e.g. 'p-0' for full-width rows inside.
	detailClassName?: string;
	// Shown under the row whether it's selected or not (e.g. an inline
	// "Hang up anyway / Cancel" before the choice is applied).
	below?: React.ReactNode;
}) => {
	// A disabled row dims its icon, title and check, but not the reason it
	// can't be chosen, which stays readable (voice round 1, F18).
	const showReason = Boolean(disabled && disabledReason);
	const dim = disabled && 'opacity-50';
	return (
		<div className={cn('relative', dividerClass, icon ? 'after:left-[52px]' : 'after:left-4')}>
			<RadioGroupPrimitive.Item
				value={value}
				disabled={disabled}
				className={cn(
					'flex min-h-11 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
					ROW_TEXT_CLASS,
					ROW_HOVER_CLASS,
					'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:hover:bg-transparent',
				)}
			>
				{icon && <IconTile icon={icon} className={cn(dim)} />}
				<span className="min-w-0 flex-1">
					<span className={cn('block', dim)}>{title}</span>
					{showReason || caption ? (
						<span className={cn('mt-0.5 block text-[13px] leading-snug', CAPTION_TONE_CLASS, !showReason && dim)}>
							{showReason ? disabledReason : caption}
						</span>
					) : null}
				</span>
				<span aria-hidden className={cn('flex h-4 w-4 shrink-0 items-center justify-center', dim)}>
					{selected && <Check className="h-4 w-4" />}
				</span>
			</RadioGroupPrimitive.Item>
			{selected && children && (
				// The chosen row's details (useArrowSafeChoice): keys, clicks and any
				// radios in here (e.g. ClosedSection's menu list) belong to them, not
				// to this list, and moving focus in settles an arrow-key choice.
				<div data-choice-details="" className={cn('pb-3 pr-4', icon ? 'pl-[52px]' : 'pl-4', detailClassName)}>
					{children}
				</div>
			)}
			{below}
		</div>
	);
};

/** The round key chip used beside options: ① ② … */
export const DigitChip = ({ digit, className }: { digit: string; className?: string }) => (
	<span
		aria-hidden
		className={cn(
			'inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-semibold tabular-nums text-foreground',
			className,
		)}
	>
		{digit}
	</span>
);

/** Inline field error under a control. Pass role="alert" for an error that appears after a save. */
export const FieldError = ({ id, children, role }: { id?: string; children: React.ReactNode; role?: 'alert' }) => (
	<p id={id} role={role} className="mt-1.5 flex items-start gap-1.5 text-[13px] leading-snug text-red-600 dark:text-red-400">
		<AlertCircle aria-hidden className="mt-px h-3.5 w-3.5 shrink-0" />
		<span>{children}</span>
	</p>
);

/** Amber "something to finish" note (always with words, never colour alone). */
export const AttentionNote = ({ children, className }: { children: React.ReactNode; className?: string }) => (
	<p className={cn('flex items-start gap-1.5 text-[13px] leading-snug text-amber-700 dark:text-amber-400', className)}>
		<AlertCircle aria-hidden className="mt-px h-3.5 w-3.5 shrink-0" />
		<span>{children}</span>
	</p>
);

/** A quiet informational box (muted surface, small text). */
export const QuietNote = ({ icon: Icon, children, className }: { icon?: React.ElementType; children: React.ReactNode; className?: string }) => (
	<div className={cn('flex gap-2 rounded-lg bg-muted/50 px-3 py-2.5 text-[13px] leading-relaxed', CAPTION_TONE_CLASS, className)}>
		{Icon && <Icon aria-hidden className="mt-px h-4 w-4 shrink-0" />}
		<div>{children}</div>
	</div>
);

/** "Callers hear: “For Sales, press 1.”" */
export const Spoken = ({ children }: { children: React.ReactNode }) => <span className="text-foreground/80">“{children}”</span>;

// ---------------------------------------------------------------------------
// Settings → Voice primitives (voice redesign spec §13.3). A tap or a switch
// saves straight away, so choices are plain buttons, never radios: Tab moves
// between rows, Enter or Space commits, and arrow keys do nothing, so
// exploring the list never changes a setting.

/** Data attributes (data-phone-menu-target, data-settings-row, …) for an element a primitive renders. */
type DataAttributes = { [key: `data-${string}`]: string | undefined };

const CheckListContext = createContext<{ busy: boolean }>({ busy: false });

/**
 * Choices that save when tapped: a <ul> of <CheckRow>s (plus a
 * <GroupSubheader as="li"> or <DisclosureRow as="li"> where needed). While
 * `busy` (a save in progress) every row is aria-disabled and ignores taps,
 * but focus stays where it is.
 */
export const CheckList = ({
	label,
	busy = false,
	children,
	className,
	id,
}: {
	label: string;
	busy?: boolean;
	children: React.ReactNode;
	className?: string;
	id?: string;
}) => (
	<CheckListContext.Provider value={{ busy }}>
		<ul id={id} aria-label={label} className={cn('m-0 list-none p-0', className)}>
			{children}
		</ul>
	</CheckListContext.Provider>
);

export interface CheckRowProps
	extends Omit<React.LiHTMLAttributes<HTMLLIElement>, 'title' | 'onSelect'>,
		LeadingSlotProps {
	icon?: React.ElementType;
	title: React.ReactNode;
	caption?: React.ReactNode;
	// What's in use now: a check, and "In use" for screen readers.
	checked?: boolean;
	checkedLabel?: string;
	// This row's save is in progress: a spinner, and "Saving" for screen readers.
	saving?: boolean;
	// Replaces the check, e.g. "Set up ›", "Off" or a skeleton bar. Part of the row's description.
	trailing?: React.ReactNode;
	// aria-disabled (still focusable) and taps are ignored. The list's `busy` does the same.
	disabled?: boolean;
	onSelect?: () => void;
	// A button beside the row's own button (never inside it), e.g. "Edit".
	secondary?: React.ReactNode;
	buttonRef?: React.Ref<HTMLButtonElement>;
	// More attributes for the row's button (data-phone-menu-target, aria-controls, aria-label, …).
	buttonProps?: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'type' | 'children'> & DataAttributes;
}

/**
 * One choice: <li><button/>[secondary]</li>. The button's name is the title;
 * its description is the caption and the trailing status.
 */
export const CheckRow = ({
	icon,
	leading,
	inset = false,
	title,
	caption,
	checked = false,
	checkedLabel = 'In use',
	saving = false,
	trailing,
	disabled = false,
	onSelect,
	secondary,
	buttonRef,
	buttonProps,
	className,
	...itemProps
}: CheckRowProps) => {
	const { busy } = useContext(CheckListContext);
	const titleId = useId();
	const captionId = useId();
	const statusId = useId();
	const inert = disabled || busy;
	const hasTrailing = trailing !== undefined && trailing !== null && trailing !== false;
	const hasStatus = saving || hasTrailing || checked;
	const { className: buttonClassName, 'aria-describedby': extraDescribedBy, ...restButtonProps } = buttonProps || {};
	const describedBy =
		[caption ? captionId : null, hasStatus ? statusId : null, extraDescribedBy].filter(Boolean).join(' ') || undefined;
	return (
		<li
			className={cn('relative flex', dividerClass, leadingDividerClass({ icon, leading, inset }), className)}
			{...itemProps}
		>
			<button
				ref={buttonRef}
				type="button"
				aria-labelledby={restButtonProps['aria-label'] ? undefined : titleId}
				{...restButtonProps}
				aria-describedby={describedBy}
				aria-disabled={inert || undefined}
				onClick={() => {
					if (!inert) onSelect?.();
				}}
				className={cn(
					'flex min-h-11 min-w-0 flex-1 items-center gap-3 px-4 py-2.5 text-left transition-colors',
					ROW_TEXT_CLASS,
					ROW_HOVER_CLASS,
					'active:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
					'aria-disabled:cursor-default aria-disabled:hover:bg-transparent aria-disabled:active:bg-transparent',
					buttonClassName,
				)}
			>
				<LeadingSlot icon={icon} leading={leading} inset={inset} />
				<span className="min-w-0 flex-1">
					<span id={titleId} className="block">
						{title}
					</span>
					{caption && (
						<span id={captionId} className={cn('mt-0.5 block text-[13px] leading-snug', CAPTION_TONE_CLASS)}>
							{caption}
						</span>
					)}
				</span>
				{hasStatus && (
					<span id={statusId} className="flex shrink-0 items-center gap-1.5 text-right text-muted-foreground">
						{saving ? (
							<>
								<Loader2 aria-hidden className="h-4 w-4 animate-spin" />
								<span className="sr-only">Saving</span>
							</>
						) : hasTrailing ? (
							trailing
						) : (
							<>
								<Check aria-hidden className="h-4 w-4 text-foreground" />
								<span className="sr-only">{checkedLabel}</span>
							</>
						)}
					</span>
				)}
			</button>
			{secondary && <div className="my-auto mr-3 flex shrink-0 items-center">{secondary}</div>}
		</li>
	);
};

export interface DisclosureRowProps
	extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title' | 'value'>,
		LeadingSlotProps {
	icon?: React.ElementType;
	title: React.ReactNode;
	// A muted 13px second line under the title ("Advanced" over "Phone
	// menu"). It's the row's description, not part of its name.
	caption?: React.ReactNode;
	value?: React.ReactNode;
	expanded: boolean;
	// Id of what it shows (aria-controls).
	controls?: string;
	// 'li' inside a <CheckList>.
	as?: 'div' | 'li';
	// Attributes for the wrapper (the row a settings search highlights).
	wrapperProps?: React.HTMLAttributes<HTMLElement> & DataAttributes;
}

/** A row that shows more in place ("Advanced · Phone menu ⌄"): a button with aria-expanded. */
export const DisclosureRow = forwardRef<HTMLButtonElement, DisclosureRowProps>(
	(
		{
			icon,
			leading,
			inset = false,
			title,
			caption,
			value,
			expanded,
			controls,
			as: Wrapper = 'div',
			wrapperProps,
			className,
			'aria-describedby': extraDescribedBy,
			...props
		},
		ref,
	) => {
		const titleId = useId();
		const captionId = useId();
		const hasCaption = hasContent(caption);
		const named = Boolean(props['aria-label'] || props['aria-labelledby']);
		const describedBy = [hasCaption ? captionId : null, extraDescribedBy].filter(Boolean).join(' ') || undefined;
		return (
			<Wrapper
				{...wrapperProps}
				className={cn('relative', dividerClass, leadingDividerClass({ icon, leading, inset }), wrapperProps?.className)}
			>
				<button
					ref={ref}
					type="button"
					aria-expanded={expanded}
					aria-controls={controls}
					aria-labelledby={hasCaption && !named ? titleId : undefined}
					aria-describedby={describedBy}
					className={cn(
						'flex min-h-11 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
						ROW_TEXT_CLASS,
						ROW_HOVER_CLASS,
						'active:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
						className,
					)}
					{...props}
				>
					<LeadingSlot icon={icon} leading={leading} inset={inset} />
					{hasCaption ? (
						<span className="min-w-0 flex-1">
							<span id={titleId} className="block truncate">
								{title}
							</span>
							<span id={captionId} className={cn('mt-0.5 block text-[13px] leading-snug', CAPTION_TONE_CLASS)}>
								{caption}
							</span>
						</span>
					) : (
						<span className="min-w-0 flex-1 truncate">{title}</span>
					)}
					{value !== undefined && value !== null && value !== '' && (
						<span className="min-w-0 truncate text-right text-muted-foreground">{value}</span>
					)}
					<ChevronDown
						aria-hidden
						className={cn(
							'h-4 w-4 shrink-0 text-muted-foreground/80 transition-transform motion-reduce:transition-none',
							expanded && 'rotate-180',
						)}
					/>
				</button>
			</Wrapper>
		);
	},
);
DisclosureRow.displayName = 'DisclosureRow';

/**
 * One switch style for grouped settings (a copy of the phone menu's): the
 * app's own colour when on, an outlined grey track when off (3:1 against the
 * cell), and an iOS-sized 51×31 switch on touch screens. The off outline is
 * a 1px border rather than a ring, so the keyboard focus ring still shows.
 */
export const SETTINGS_SWITCH_CLASS = cn(
	'data-[state=unchecked]:border data-[state=unchecked]:border-zinc-500 data-[state=unchecked]:bg-zinc-300',
	'dark:data-[state=unchecked]:border-zinc-400 dark:data-[state=unchecked]:bg-zinc-700',
	'touch:h-[31px] touch:w-[51px] touch:[&>span]:h-[27px] touch:[&>span]:w-[27px] touch:data-[state=checked]:[&>span]:translate-x-5',
);

/** A whole row that toggles its switch (a <label> for it), like an iOS settings row. Add after:left-4 or after:left-[52px]. */
export const SWITCH_ROW_CLASS = cn(
	'relative flex min-h-11 w-full cursor-pointer items-center gap-3 px-4 py-2.5',
	ROW_TEXT_CLASS,
	'after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:h-px after:bg-border/70 last:after:hidden',
	'has-[:disabled]:cursor-default',
);

export interface SwitchRowProps {
	title: React.ReactNode;
	caption?: React.ReactNode;
	icon?: React.ElementType;
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
	disabled?: boolean;
	// A save is in progress: a spinner next to the switch.
	saving?: boolean;
	// Muted words before the switch, e.g. "Required".
	note?: React.ReactNode;
	// The switch's id (one is made up otherwise).
	id?: string;
	// The switch's name when the title isn't plain text. Starts with the visible label.
	ariaLabel?: string;
	// E.g. the group's footerId, so the footer is read with the switch.
	describedBy?: string;
	className?: string;
	// Attributes for the row (data-settings-row, …).
	rowProps?: React.LabelHTMLAttributes<HTMLLabelElement> & DataAttributes;
}

/** A row whose whole width toggles its switch (a <label htmlFor>). */
export const SwitchRow = ({
	title,
	caption,
	icon,
	checked,
	onCheckedChange,
	disabled = false,
	saving = false,
	note,
	id,
	ariaLabel,
	describedBy,
	className,
	rowProps,
}: SwitchRowProps) => {
	const generatedId = useId();
	const captionId = useId();
	const switchId = id ?? generatedId;
	const name = ariaLabel ?? (typeof title === 'string' ? title : undefined);
	const describedByIds = [caption ? captionId : null, describedBy].filter(Boolean).join(' ') || undefined;
	return (
		<label
			{...rowProps}
			htmlFor={switchId}
			className={cn(SWITCH_ROW_CLASS, icon ? 'after:left-[52px]' : 'after:left-4', className, rowProps?.className)}
		>
			{icon && <IconTile icon={icon} />}
			<span className="min-w-0 flex-1">
				<span className="block leading-normal">{title}</span>
				{caption && (
					<span id={captionId} className={cn('mt-0.5 block text-[13px] leading-snug', CAPTION_TONE_CLASS)}>
						{caption}
					</span>
				)}
			</span>
			{saving && <Loader2 aria-hidden className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
			{note && <span className={cn('shrink-0 text-[13px]', CAPTION_TONE_CLASS)}>{note}</span>}
			<Switch
				id={switchId}
				aria-label={name}
				aria-describedby={describedByIds}
				checked={checked}
				disabled={disabled}
				className={SETTINGS_SWITCH_CLASS}
				onCheckedChange={onCheckedChange}
			/>
		</label>
	);
};

export interface ValueSelectOption {
	value: string;
	label: string;
}

export interface ValueSelectProps extends LeadingSlotProps {
	// The pop-up's name, starting with the visible label ("After hours").
	ariaLabel: string;
	value: string;
	options: ValueSelectOption[];
	onValueChange: (value: string) => void;
	// With a title it's a whole row, and the whole row opens the pop-up
	// (voice round 2, F6): the title on the left, the value ⌄ on the right.
	// Without one, only the value ⌄ (for a row that lays itself out).
	title?: React.ReactNode;
	icon?: React.ElementType;
	disabled?: boolean;
	// A save is in progress: a spinner before the value.
	saving?: boolean;
	id?: string;
	// The row, with a title.
	className?: string;
	// The value ⌄, without a title.
	triggerClassName?: string;
}

const VALUE_TRIGGER_CLASS = cn(
	'h-11 w-auto min-w-0 max-w-full justify-end gap-1 rounded-md border-0 bg-transparent px-1 text-right text-muted-foreground shadow-none',
	ROW_TEXT_CLASS,
	'[@media(hover:hover)]:hover:text-foreground focus:ring-2 focus:ring-ring sm:h-9 touch:h-11',
);

/** A whole row that is a button (a pop-up's trigger, a native picker's box), like RowButton. */
const ROW_CONTROL_CLASS = cn(
	'relative flex min-h-11 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
	ROW_TEXT_CLASS,
	ROW_HOVER_CLASS,
	'active:bg-muted/60',
	dividerClass,
);

/**
 * The menu opens under the value, as wide as its longest choice rather than
 * the whole row (the viewport's min-width would otherwise be the row's).
 */
const VALUE_ROW_CONTENT_CLASS = 'min-w-[10rem] [&_[data-radix-select-viewport]]:min-w-0';

const SelectOptions = ({ options }: { options: ValueSelectOption[] }) => (
	<>
		{options.map((option) => (
			<SelectItem key={option.value} value={option.value}>
				{option.label}
			</SelectItem>
		))}
	</>
);

/**
 * A pop-up whose value sits in a row ("After hours   Keep ringing ⌄").
 * Changing it saves straight away. For the Voice page; inside a sheet use
 * <SelectRow> (a native picker), which Esc can't leave stuck.
 */
export const ValueSelect = ({
	ariaLabel,
	value,
	options,
	onValueChange,
	title,
	icon,
	leading,
	inset = false,
	disabled = false,
	saving = false,
	id,
	className,
	triggerClassName,
}: ValueSelectProps) => {
	if (!title) {
		return (
			<Select value={value} onValueChange={onValueChange} disabled={disabled}>
				<SelectTrigger id={id} aria-label={ariaLabel} className={cn(VALUE_TRIGGER_CLASS, triggerClassName)}>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectOptions options={options} />
				</SelectContent>
			</Select>
		);
	}
	return (
		<Select value={value} onValueChange={onValueChange} disabled={disabled}>
			<SelectPrimitive.Trigger
				id={id}
				aria-label={ariaLabel}
				className={cn(
					ROW_CONTROL_CLASS,
					'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-muted/40',
					leadingDividerClass({ icon, leading, inset }),
					className,
				)}
			>
				<LeadingSlot icon={icon} leading={leading} inset={inset} />
				<span className="shrink-0">{title}</span>
				<span className="ml-auto flex min-w-0 items-center justify-end gap-1.5 text-muted-foreground">
					{saving && <Loader2 aria-hidden className="h-4 w-4 shrink-0 animate-spin" />}
					<span className="min-w-0 truncate">
						<SelectValue />
					</span>
					<SelectPrimitive.Icon asChild>
						<ChevronDown aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground/80" />
					</SelectPrimitive.Icon>
				</span>
			</SelectPrimitive.Trigger>
			<SelectContent align="end" className={VALUE_ROW_CONTENT_CLASS}>
				<SelectOptions options={options} />
			</SelectContent>
		</Select>
	);
};

export interface SelectRowProps extends LeadingSlotProps {
	// The visible label; also the picker's name unless `ariaLabel` is given.
	title: React.ReactNode;
	value: string;
	options: ValueSelectOption[];
	onValueChange: (value: string) => void;
	// The picker's name when the title isn't plain text. Starts with the visible label.
	ariaLabel?: string;
	disabled?: boolean;
	// A save is in progress: a spinner before the value.
	saving?: boolean;
	// The <select>'s id.
	id?: string;
	className?: string;
}

/**
 * ValueSelect for inside a sheet ("Language   Spanish ⌄"): the device's own
 * <select> covers the whole row, so the whole row opens it and the row
 * looks like ValueSelect. A Radix Select inside the sheet sat on another
 * copy of Radix's layer stack, so Esc on an open menu closed the sheet too
 * and left the page unclickable (voice round 1, F1); a native picker keeps
 * Esc to itself. 16px on phones, so iOS doesn't zoom in on it.
 */
export const SelectRow = ({
	title,
	value,
	options,
	onValueChange,
	ariaLabel,
	icon,
	leading,
	inset = false,
	disabled = false,
	saving = false,
	id,
	className,
}: SelectRowProps) => {
	const generatedId = useId();
	const titleId = useId();
	const selectId = id ?? generatedId;
	const label = options.find((option) => option.value === value)?.label ?? '';
	return (
		<div
			className={cn(
				ROW_CONTROL_CLASS,
				'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-ring has-[:disabled]:opacity-50',
				'has-[:disabled]:hover:bg-transparent has-[:disabled]:active:bg-transparent',
				leadingDividerClass({ icon, leading, inset }),
				className,
			)}
		>
			<LeadingSlot icon={icon} leading={leading} inset={inset} />
			<span id={titleId} className="shrink-0">
				{title}
			</span>
			<span aria-hidden className="ml-auto flex min-w-0 items-center justify-end gap-1.5 text-muted-foreground">
				{saving && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
				<span className="min-w-0 truncate">{label}</span>
				<ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground/80" />
			</span>
			<select
				id={selectId}
				aria-label={ariaLabel}
				aria-labelledby={ariaLabel ? undefined : titleId}
				value={value}
				disabled={disabled}
				onChange={(event) => onValueChange(event.target.value)}
				className="absolute inset-0 h-full w-full cursor-pointer appearance-none text-base opacity-0 focus:outline-none disabled:cursor-default sm:text-sm"
			>
				{options.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
		</div>
	);
};

export type MeterTone = 'normal' | 'warning' | 'over';

/** Below 80%, from 80% and from 100% of the allowance. */
export const meterTone = (value: number, max: number): MeterTone => {
	if (!(max > 0)) return 'normal';
	const ratio = value / max;
	if (ratio >= 1) return 'over';
	if (ratio >= 0.8) return 'warning';
	return 'normal';
};

const METER_FILL_CLASS: Record<MeterTone, string> = {
	normal: 'bg-foreground/70',
	warning: 'bg-amber-500',
	over: 'bg-red-600',
};

/**
 * A 6px usage bar with role="meter". Colour always comes with words: the
 * row around it says "687 left" or "42 over".
 */
export const Meter = ({
	value,
	max,
	valueText,
	label,
	labelledBy,
	id,
	className,
}: {
	value: number;
	max: number;
	// "313 of 1,000 minutes"; when over, "1,042 minutes, 42 over your 1,000".
	valueText: string;
	label?: string;
	labelledBy?: string;
	id?: string;
	className?: string;
}) => {
	const safeMax = max > 0 ? max : 1;
	const now = Math.min(Math.max(value, 0), safeMax);
	return (
		// biome-ignore lint/a11y/useSemanticElements: a styled bar; <meter> can't be styled the same in every browser
		<div
			id={id}
			role="meter"
			aria-label={label}
			aria-labelledby={labelledBy}
			aria-valuemin={0}
			aria-valuemax={max}
			aria-valuenow={Math.min(Math.max(value, 0), max)}
			aria-valuetext={valueText}
			className={cn('h-1.5 w-full overflow-hidden rounded-full bg-muted dark:bg-white/10', className)}
		>
			<div
				className={cn(
					'h-full rounded-full transition-[width] motion-reduce:transition-none',
					METER_FILL_CLASS[meterTone(value, max)],
				)}
				style={{ width: `${(now / safeMax) * 100}%` }}
			/>
		</div>
	);
};
